import { useEffect } from 'react';
import { Form } from 'react-final-form';
import { useNavigate } from 'react-router-dom';
import { datadogLogs } from '@datadog/browser-logs';
import Loader from 'components/Loader';
import { LoadError } from 'components/LoadError';
import { TableSwitchCell } from 'components/TableSwitchCell';
import { has_permission } from 'config/permissions';
import { getApiDebuggingId, getApiErrorStatus } from 'helpers/rtk-utils';
import { numberWithDot } from 'helpers/utils';
import { useAdminAuth } from 'hooks/useAdminAuth';
import { useGetClientFiltersQuery } from 'services/clientFiltersApi';
import { getSelectedClient } from 'services/storage';
import { FILTERS_LOAD_ERROR, HDD_CLIENT_ID, OATH_CLIENT_ID } from './constants';
import { FilterOrderList } from './FilterOrderList';
import { FiltersNotice } from './FiltersNotice';
import { FilterOrderValues } from './types';
import { useSaveFilterStatus } from './useSaveFilterStatus';
import { getFiltersNotice, haveSameFilterOrder, isNotFoundError } from './utils';

const Filters = () => {
  const { clientId, activeRole, hasClient } = useAdminAuth();
  const canViewFilters = has_permission('FILTERS_ROUTE', 'R', activeRole);
  // The site shows Oath's filters to HDD, so HDD's own rows never reach donors.
  const isReadOnly = clientId === HDD_CLIENT_ID;
  const filtersClientId = isReadOnly ? OATH_CLIENT_ID : (clientId ?? 0);
  const navigate = useNavigate();
  const saveFilterStatus = useSaveFilterStatus(filtersClientId);

  // currentData (not data) so a client switch never briefly shows the previous client's filters.
  const {
    currentData: data,
    isFetching,
    error,
    refetch,
  } = useGetClientFiltersQuery(
    { clientId: filtersClientId },
    { skip: !hasClient || !canViewFilters }
  );

  const hasNoCart = isNotFoundError(error);
  const hasLoadError = Boolean(error) && !hasNoCart;

  useEffect(() => {
    if (!hasLoadError) return;
    datadogLogs.logger.error('Failed to load client filters', {
      client_id: filtersClientId,
      status: getApiErrorStatus(error),
      debugging_id: getApiDebuggingId(error),
    });
  }, [hasLoadError, error, filtersClientId]);

  if (!activeRole) return null;

  if (!canViewFilters) {
    return (
      <div className="content-full-width">
        <h1 className="title-permission">
          Sorry, you don&apos;t have permission to see this screen.
        </h1>
      </div>
    );
  }

  const clientName = hasClient ? getSelectedClient()?.name : undefined;
  const filters = data?.filters ?? [];
  const initialOrder: FilterOrderValues = { filter_ids: filters.map(({ filter_id }) => filter_id) };
  const goToFilterDetail = (filterId: string) => navigate(`/filters_detail?filter_id=${filterId}`);
  const renderStatusSwitch = (filterId: string) => {
    const filter = filters.find(({ filter_id }) => filter_id === filterId);
    if (!filter) return null;
    return (
      <TableSwitchCell
        value={filter.status === 'ACTIVE'}
        onText="ACTIVE"
        offText="PAUSED"
        saveLabel={`Save status of ${filter.filter_name}`}
        onSave={isActive => saveFilterStatus(filterId, isActive)}
        isWide
        disabled={isReadOnly}
      />
    );
  };
  const notice = getFiltersNotice(clientId, hasNoCart, clientName);
  const canCreateFilter = !isReadOnly && !hasNoCart && !hasLoadError && !isFetching;

  const content = hasLoadError ? (
    <LoadError message={FILTERS_LOAD_ERROR} onRetry={refetch} />
  ) : !hasClient ? (
    <span className="oath-body">Select a client to see their filters.</span>
  ) : (
    <>
      {notice && <FiltersNotice message={notice} />}
      {!isFetching && data && (
        // The drag list reads form fields, so the order lives in a form; each drop saves on its own.
        // Keyed by client: the navbar picker can switch clients without unmounting this page.
        <Form<FilterOrderValues>
          key={filtersClientId}
          onSubmit={() => undefined}
          initialValues={initialOrder}
          // A status save keeps the ids, so it never resets an order being saved; an order save does.
          initialValuesEqual={haveSameFilterOrder}
          render={() => (
            <FilterOrderList
              clientId={filtersClientId}
              filters={filters}
              isReadOnly={isReadOnly}
              onEditFilter={isReadOnly ? undefined : goToFilterDetail}
              renderStatusSwitch={renderStatusSwitch}
            />
          )}
        />
      )}
    </>
  );

  return (
    <>
      {isFetching && <Loader type="dots" />}
      <div className="row filters-row client-filters__nav-spacer" />
      <div className="content-full-width client-filters__content">
        <div className="row custom-header-container">
          <div className="grid-column col-lg-9 col-md-8 col-sm-12">
            <span className="oath-h3 inkBlue">
              {clientName ? `${clientName} Filters` : 'Filters'}
            </span>
            {hasClient && (
              <span className="oath-body inkBlue">{numberWithDot(filters.length)} Total</span>
            )}
          </div>
          {hasClient && (
            <div className="col-lg-3 col-md-4 col-sm-12 new-page-button">
              <button
                type="button"
                className="btn-inkBlue btn btn-secondary btn-block"
                onClick={() => navigate('/filters_detail')}
                disabled={!canCreateFilter}
              >
                Create New Filter
              </button>
            </div>
          )}
        </div>
        <div className="row">
          <div className="col-lg-12">{content}</div>
        </div>
      </div>
    </>
  );
};

export default Filters;
